import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Juror } from '../../../../redux/contest/types';
import { BrandContestJurorAvatar } from './BrandContestJurorAvatar';
import { FullScreenMoreMenu } from '../../../More/FullScreenMoreMenu';
import { TMoreMenuItem } from '../../../More/more.data';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CINEMA_ON_SURFACE, ghostBorderSx } from '../../../WatchPage/watchCinemaTheme';
import { brandContestTableCellTextSx } from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import { BrandContestJurorProgressRing } from './BrandContestJurorProgressRing';
import { brandJurorGridSx } from './brandJurorCardLayout';
import { inviteStatusCompactVisual, pulseDot } from './brandJurorCardShared';

const VALUE_SX = {
    ...brandContestTableCellTextSx,
    fontSize: 14,
    color: CINEMA_ON_SURFACE,
} as const;

const METRIC_SX = {
    ...VALUE_SX,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontWeight: 600,
    fontSize: 15,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
} as const;

const MORE_ICON_BUTTON_SX = {
    p: 0.5,
    minWidth: 0,
    width: 40,
    height: 40,
    borderRadius: 2,
    bgcolor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
} as const;

export interface BrandContestJurorCardDesktopProps {
    juror: Juror;
    menuItems: TMoreMenuItem[];
    onMenuClick: (type: string, juror: Juror) => void;
}

const BrandContestJurorCardDesktop: React.FC<BrandContestJurorCardDesktopProps> = ({
    juror,
    menuItems,
    onMenuClick,
}) => {
    const status = inviteStatusCompactVisual(juror.accepted_invite);
    const person = juror.juror;

    return (
        <Box
            sx={{
                ...brandJurorGridSx,
                py: 2.5,
                minHeight: 96,
            }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                    justifySelf: 'start',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%',
                }}>
                <Box sx={{ flexShrink: 0 }}>
                    <BrandContestJurorAvatar user={person} acceptedInvite={Boolean(juror.accepted_invite)} size={44} />
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography
                        noWrap
                        sx={{
                            ...VALUE_SX,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {person?.firstName} {person?.lastName}
                    </Typography>
                    <Typography
                        noWrap
                        sx={{
                            ...VALUE_SX,
                            fontSize: 12,
                            color: `${CINEMA_ON_SURFACE}b3`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        @{person?.userName ?? 'unknown'}
                    </Typography>
                </Stack>
            </Stack>

            <Box sx={{ justifySelf: 'center' }}>
                <BrandContestJurorProgressRing
                    percentage={juror.judgment_percentage ?? 0}
                    size={48}
                    showLabel={false}
                />
            </Box>

            <Typography sx={{ ...METRIC_SX, justifySelf: 'center', textAlign: 'center' }}>
                {juror.total_assigned ?? 0}
            </Typography>

            <Typography sx={{ ...METRIC_SX, justifySelf: 'center', textAlign: 'center' }}>
                {juror.judged_submissions ?? 0}
            </Typography>

            <Box sx={{ justifySelf: 'center' }}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: status.bg,
                        color: status.color,
                        border: `1px solid ${status.border}`,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 700,
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                    }}>
                    <Box
                        sx={{
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: status.dot,
                            boxShadow: `0 0 6px ${status.dot}`,
                            ...(status.pulse ? { animation: `${pulseDot} 2s ease-in-out infinite` } : {}),
                        }}
                    />
                    {status.label}
                </Box>
            </Box>

            <Box sx={{ justifySelf: 'center' }}>
                <Box
                    sx={{
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        border: '1px solid rgba(153, 144, 124, 0.2)',
                        bgcolor: 'rgba(42, 42, 42, 0.4)',
                        backdropFilter: 'blur(24px)',
                        WebkitBackdropFilter: 'blur(24px)',
                        ...ghostBorderSx,
                    }}>
                    <FullScreenMoreMenu
                        menu={menuItems}
                        onClick={type => onMenuClick(type, juror)}
                        customIconSx={MORE_ICON_BUTTON_SX}
                        customMoreIcon={
                            <MoreHorizIcon
                                sx={{
                                    transform: 'rotate(180deg)',
                                    color: CINEMA_ON_SURFACE,
                                    fontSize: 22,
                                }}
                            />
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};

export { BrandContestJurorCardDesktop };
